import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Globe2,
  FlaskConical,
  Sprout,
  Handshake,
  ShieldCheck,
  Beaker,
  Leaf,
} from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1608571424634-58ae03e6edcf?w=1600&q=85";

export default function About() {
  usePageMeta({
    title: "Our Story — Simply Earth Ambassador · Pure Essential Oils | DC Gift World Too",
    description: "Proud Simply Earth Ambassador — pure essential oils sourced directly from farmers, GC/MS lab-tested, and part of the fight to end human trafficking. Hand-bottled in Limon, Colorado.",
  });
  return (
    <div data-testid="about-page">
      {/* Intro */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-12 text-center">
        <p className="eyebrow mb-4">Our story</p>
        <h1 className="font-serif text-5xl sm:text-6xl text-[var(--ink-forest)] tracking-tight leading-[1.05]">
          Loved from
          <span className="italic text-[var(--terracotta)]"> farm to bottle.</span>
        </h1>
        <p className="mt-6 text-[var(--muted-fg)] leading-relaxed text-lg max-w-2xl mx-auto">
          Every bottle on our shelf has a story — a farmer, a place, a season. We curate the parts
          we love most and pass them on to you with the same care they were grown with.
        </p>
      </section>

      {/* Hero image with quote overlay */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="relative rounded-3xl overflow-hidden">
          <img src={HERO_IMG} alt="Amber essential oil bottles" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[var(--ink-forest)]/70 via-[var(--ink-forest)]/20 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 lg:bottom-14 lg:left-14 max-w-xl">
            <p className="eyebrow text-[var(--gold)] mb-3">Our promise</p>
            <p className="font-serif text-3xl lg:text-4xl text-white leading-tight">
              &ldquo;We work directly with farmers — no middlemen, no shortcuts, no compromises.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Sourcing philosophy */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="eyebrow mb-3">Sourcing</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
              Each plant in its native home.
            </h2>
            <p className="mt-5 text-[var(--muted-fg)] leading-relaxed">
              Lavender flourishes in the south of France. Eucalyptus belongs to Australia.
              Frankincense is hand-tapped in Somalia. We source globally because that&apos;s the only
              honest way to bring you a true single-note essential oil.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <Pillar
              icon={Handshake}
              title="Direct from the farm"
              body="We work directly with growers, cutting out brokers and middlemen so the price stays fair — for the farmer and for you."
            />
            <Pillar
              icon={Leaf}
              title="Steam distilled & cold pressed"
              body="Time-honored extraction methods that preserve each plant's full character. No solvents, no shortcuts."
              raised
            />
            <Pillar
              icon={Globe2}
              title="Sourced globally"
              body="Each oil from the place it grows truest — climate, soil, and altitude all play their quiet part in the final scent."
              raised
            />
            <Pillar
              icon={Sprout}
              title="Hand bottled in the USA"
              body="Filled and labeled in a small commercial kitchen — the same way your grandmother filled jars of preserves."
            />
          </div>
        </div>
      </section>

      {/* Quality */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 lg:mt-32">
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-[2rem] p-10 lg:p-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-3">Quality control</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
                Tested for 100% purity.
                <span className="italic text-[var(--terracotta)]"> Every batch.</span>
              </h2>
              <p className="mt-5 text-[var(--muted-fg)] leading-relaxed">
                Every batch we receive is sent to an independent third-party GC/MS lab — a
                technique that breaks each oil into its molecular fingerprint to confirm purity and
                catch any adulteration. If a batch doesn&apos;t pass, it doesn&apos;t make it onto the shelf.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
              <QualityStat icon={Beaker} value="3rd-party" label="GC/MS lab tested" />
              <QualityStat icon={ShieldCheck} value="100%" label="Purity guaranteed" />
              <QualityStat icon={FlaskConical} value="Every" label="Batch verified" />
            </div>
          </div>
        </div>
      </section>

      {/* Amber glass */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1560521698-96781dae0da4?w=1200&q=85"
              alt="Amber glass essential oil bottles"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="eyebrow mb-3">Bottled with care</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
              Why amber glass.
            </h2>
            <div className="mt-6 space-y-4 text-[var(--muted-fg)] leading-relaxed">
              <p>
                Sunlight is an essential oil&apos;s quiet enemy. UV rays break down the delicate
                volatile compounds that make each oil what it is — that&apos;s why we bottle every
                single oil and blend in deep amber glass.
              </p>
              <p>
                Amber filters out the wavelengths that degrade the oil, keeping it as fresh and
                potent the day you open it as the day it was bottled.
              </p>
            </div>
            <Link
              to="/shop?category=essential-oils"
              className="mt-8 inline-flex items-center gap-2 bg-[var(--terracotta)] text-white rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
              data-testid="about-shop-oils-cta"
            >
              Shop the oils <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Simply Earth Ambassador */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24" data-testid="about-simply-earth">
        <div className="bg-[var(--bg-cream)] border-2 border-[var(--terracotta)]/25 rounded-[2rem] p-10 lg:p-14">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-3 text-[var(--terracotta)]">A partnership we&apos;re proud of</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
                Our oils are sourced through
                <span className="italic text-[var(--terracotta)]"> Simply Earth.</span>
              </h2>
              <p className="mt-6 text-[var(--muted-fg)] leading-relaxed">
                We are proud <strong>Simply Earth Ambassadors</strong> — a small, family-run
                company that shares our love for pure, ethically sourced essential oils. Every
                bottle we carry is GC/MS lab-tested for 100% purity.
              </p>
              <p className="mt-4 text-[var(--muted-fg)] leading-relaxed">
                Simply Earth donates <strong>13% of their profits</strong> to organizations working
                to end <strong>human trafficking</strong> and rescue survivors. When you buy an
                oil from our shelf, you&apos;re quietly part of that mission too.
              </p>
              <a
                href="https://simplyearth.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[var(--terracotta)] hover:text-[var(--terracotta-deep)] underline underline-offset-4"
                data-testid="about-simply-earth-link"
              >
                Learn about Simply Earth <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 text-center">
                  <Handshake className="w-6 h-6 text-[var(--terracotta)] mx-auto mb-3" />
                  <p className="font-serif text-2xl text-[var(--ink-forest)]">13%</p>
                  <p className="text-xs text-[var(--muted-fg)] mt-1">Of profits donated</p>
                </div>
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 text-center">
                  <ShieldCheck className="w-6 h-6 text-[var(--terracotta)] mx-auto mb-3" />
                  <p className="font-serif text-2xl text-[var(--ink-forest)]">Ends</p>
                  <p className="text-xs text-[var(--muted-fg)] mt-1">Human trafficking</p>
                </div>
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 text-center col-span-2">
                  <Leaf className="w-6 h-6 text-[var(--terracotta)] mx-auto mb-3" />
                  <p className="font-serif text-xl text-[var(--ink-forest)]">Pure, ethical, tested</p>
                  <p className="text-xs text-[var(--muted-fg)] mt-1">Every single batch</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 lg:mt-32 mb-12">
        <div className="bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-[2rem] p-10 lg:p-14 text-center">
          <p className="eyebrow text-[var(--gold)] mb-3">Made with care</p>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight">
            From a small kitchen — to your shelf.
          </h2>
          <p className="mt-4 text-[var(--bg-cream)]/80 max-w-xl mx-auto">
            Hand-curated essential oils, DIY supplies, and storybook garden gifts — gathered for
            the kind of home that smells like cinnamon and sounds like wind in the trees.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link
              to="/shop"
              className="bg-[var(--gold)] text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta)] hover:text-white transition-colors"
              data-testid="about-shop-cta"
            >
              Browse the shop
            </Link>
            <Link
              to="/track"
              className="border border-[var(--bg-cream)]/30 text-[var(--bg-cream)] rounded-full px-7 py-3 text-sm hover:bg-[var(--bg-cream)] hover:text-[var(--ink-forest)] transition-colors"
            >
              Track your order
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Pillar({ icon: Icon, title, body, raised }) {
  return (
    <div
      className={`bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 ${
        raised ? "lg:translate-y-6" : ""
      }`}
    >
      <div className="w-10 h-10 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[var(--terracotta)]" />
      </div>
      <h3 className="font-serif text-xl text-[var(--ink-forest)] mb-2">{title}</h3>
      <p className="text-sm text-[var(--muted-fg)] leading-relaxed">{body}</p>
    </div>
  );
}

function QualityStat({ icon: Icon, value, label }) {
  return (
    <div className="bg-[var(--bg-cream)] border border-[var(--border)] rounded-2xl p-5 text-center">
      <Icon className="w-5 h-5 text-[var(--terracotta)] mx-auto mb-3" />
      <p className="font-serif text-2xl text-[var(--ink-forest)]">{value}</p>
      <p className="text-xs text-[var(--muted-fg)] mt-1">{label}</p>
    </div>
  );
}
